import { useMemo, useCallback, useRef } from 'react';
import { INTERNAL_SHARED_DATA_ID } from '../constants/common';
import { wrapSharedDataWithId } from '../helpers/shared-data-utils';

const useSharedData = (getSharedData, resolveSharedData) => {
  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const sharedData = useMemo(() => getSharedData(), []);
  const isPersisting = useRef(false);

  const { internalSharedData, externalSharedData } = useMemo(() => {
    const { [INTERNAL_SHARED_DATA_ID]: internalData, ...externalData } = sharedData;

    return {
      internalSharedData: internalData,
      externalSharedData: externalData,
    };
  }, [sharedData]);

  const lastInternalSharedDataRef = useRef(internalSharedData ?? {});

  const persistInternalSharedData = useCallback(
    (data, extraSharedData = {}) => {
      isPersisting.current = true;

      const mergedData = {
        ...lastInternalSharedDataRef.current,
        ...data,
      };

      lastInternalSharedDataRef.current = mergedData;

      resolveSharedData({
        ...externalSharedData,
        ...extraSharedData,
        ...wrapSharedDataWithId(INTERNAL_SHARED_DATA_ID, mergedData),
      });

      const reset = () => {
        isPersisting.current = false;
      };

      return Promise.resolve({ reset });
    },
    [externalSharedData, resolveSharedData]
  );

  const checkPersisting = useCallback(() => isPersisting.current, []);

  return {
    internalSharedData,
    externalSharedData,
    persistInternalSharedData,
    checkPersisting,
  };
};

export default useSharedData;
